import { inject, injectable } from "@launchtray/tsyringe-async";
import { CrossmintPaymentTimeout, CrossmintSdk } from "../CrossmintSdk";
import { Bindable, NotifyProperty, nameof } from "../../shared/bindable-data";
import { ILogger } from "../../cityversetycoon/logger/ILogger";
import { ThirdWeb } from "../thirdWeb/ThirdWeb";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";
import { Web3Config } from "../config/Web3Config";
import { INetworkClient } from "../../cityversetycoon/networking/INetworkClient";
import { Chain, createThirdwebClient } from "thirdweb";
import { NFTBuilding } from "../nfts/NFTBuilding";
import { NFTPackage } from "../nfts/NFTPackage";
import { PaymentResult } from "../PaymentResult";
import { ChainUtils } from "../utils/ChainUtils";
import { ReservoirSDK } from "../utils/ReservoirSDK";
import { MintRequestDto } from "../../cityversetycoon/networking/data/responses/MintRequestDto";
import { MintRequest } from "../thirdWeb/MintRequest";
import { NFTGiftPackage } from "../nfts/NFTGiftPackage";
import { PaymentUrlResolver } from "../utils/PaymentUrlResolver";
import { CancellationToken } from "async/CancellationToken";
import { ExternalWalletType } from "../externalWallets/ExternalWalletType";
import { IExternalWallet } from "../externalWallets/IExternalWallet";
import { MetaMask } from "../externalWallets/MetaMask";
import { GameInfo } from "../../analytics/GameInfo";
import { VisibilityTimeoutObserver } from "../../tools/VisibilityTimeoutObserver";
import { CoinBase } from "../externalWallets/CoinBase";
import { InventoriesHolder } from "./InventoriesHolder";
import { utils } from "pixi.js";
import { AsyncOperationNotification } from "../../cityversetycoon/scenes/common/components/AsyncOperationNotification";
import { NotifyMap } from "by/gamefactory/shared/bindable-data/binding_data/NotifyMap";
import { ThirdWebAnalytic } from "../utils/ThirdWebAnalytic";

@injectable()
export class Web3Model extends Bindable {
    private static readonly WALLET_OPERATIONS_DELAY = 8000;

    public readonly walletAutoConnected: NotifyProperty<boolean>;
    public readonly connected: NotifyProperty<boolean>;
    public readonly address: NotifyProperty<string>;
    public readonly chainName: NotifyProperty<string>;
    public readonly supportedChainSelected: NotifyProperty<boolean>;
    public readonly balanceUSDC: NotifyProperty<string>;
    public readonly balanceMON: NotifyProperty<string>;
    // @giorgos - I've added the below as a generalisation of token balances for different tokens
    public readonly erc20BalancesOnBase: NotifyMap<string, string>;
    public readonly erc20BalancesOnL1: NotifyMap<string, string>;

    private readonly thirdWeb: ThirdWeb;
    private readonly crossmint: CrossmintSdk;
    private readonly reservoirListInUtil: ReservoirSDK;

    private readonly timeoutObserver: VisibilityTimeoutObserver;
    private readonly externalWallets: Map<ExternalWalletType, IExternalWallet>;

    public get smartWalletAddress(): string {
        return this.thirdWeb.smartWalletAddress;
    }

    public get email(): string {
        return this.thirdWeb.email;
    }

    public constructor(
        private readonly inventory: InventoriesHolder,
        private readonly web3Config: Web3Config,
        private readonly variableSaver: VariableSaver,
        private readonly gameInfo: GameInfo,
        private readonly asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger")
        private readonly logger: ILogger,
        @inject("INetworkClient")
        private readonly networkClient: INetworkClient,
        thirdWebAnalytic: ThirdWebAnalytic,
        paymentUrlResolver: PaymentUrlResolver
    ) {
        super();
        this.web3Config = web3Config;
        this.logger = logger;
        this.externalWallets = new Map();
        this.timeoutObserver = new VisibilityTimeoutObserver(Web3Model.WALLET_OPERATIONS_DELAY);
        this.crossmint = new CrossmintSdk(web3Config, gameInfo, networkClient, paymentUrlResolver, variableSaver, logger);

        const thirdWebclient = createThirdwebClient({ clientId: web3Config.thirdWebclientId });
        this.reservoirListInUtil = new ReservoirSDK(web3Config, thirdWebclient, thirdWebAnalytic, logger);
        this.thirdWeb = new ThirdWeb(web3Config, thirdWebclient, variableSaver, thirdWebAnalytic, logger);

        this.connected = new NotifyProperty(this, nameof(this, "connected"), false);
        this.address = new NotifyProperty(this, nameof(this, "address"), undefined);
        this.chainName = new NotifyProperty(this, nameof(this, "chainName"), undefined);
        this.supportedChainSelected = new NotifyProperty(this, nameof(this, "supportedChainSelected"), false);
        this.walletAutoConnected = new NotifyProperty(this, nameof(this, "walletAutoConnected"), undefined);
        this.balanceUSDC = new NotifyProperty(this, nameof(this, "balanceUSDC"), "0.0000");
        this.balanceMON = new NotifyProperty(this, nameof(this, "balanceMON"), "0.0000");
        this.erc20BalancesOnBase = new NotifyMap(this, nameof(this, "erc20BalancesOnBase"));
        this.erc20BalancesOnL1 = new NotifyMap(this, nameof(this, "erc20BalancesOnL1"));
        this.thirdWeb.onDataChanged.add(this.updateWalletData, this);
    }

    public async autoConnect(): Promise<void> {
        try {
            if (await this.thirdWeb.autoConnect()) {
                this.updateWalletData();
                this.walletAutoConnected.setValue(true);
                this.thirdWeb.waitSmartWalletConnection().then(() => {
                    this.inventory.mainWalletAddress = this.smartWalletAddress;
                });
                return;
            }
        } catch (e: unknown) {
            this.logger.debug(`Exception on auto connect wallet: ${e}`);
            if (this.connected.value) {
                this.updateWalletData();
                this.walletAutoConnected.setValue(true);
                return;
            }
        }

        this.walletAutoConnected.setValue(false);
    }

    public async requestVerificationCode(email: string): Promise<boolean> {
        return await this.thirdWeb.sendVereficationEmail(email);
    }

    public async requestPassKeyConnection(): Promise<boolean> {
        return await this.thirdWeb.sendPasskeyLoginRequest();
    }

    public async hasStoredPasskey(): Promise<boolean> {
        return await this.thirdWeb.hasStoredPassekey();
    }

    public async createWallet(email: string, verificationCode: string): Promise<boolean> {
        if (this.thirdWeb.connected) {
            this.logger.warn(`Attemp of creating wallet, user already connected`);
            return;
        }
        try {
            if (await this.thirdWeb.createWallet(email, verificationCode)) {
                this.updateWalletData();
                await this.thirdWeb.waitSmartWalletConnection();
                this.inventory.mainWalletAddress = this.smartWalletAddress ?? this.address.value;
                return true;
            }
            return false;
        } catch (e: unknown) {
            this.logger.error("Exception on creating wallet", e);
            return false;
        }
    }

    public async createPasskeyWallet(isNewPasskeyFlow?: boolean): Promise<boolean> {
        if (this.thirdWeb.connected) {
            this.logger.warn(`Attemp of creating passkey wallet, user already connected`);
            return;
        }
        try {
            if (await this.thirdWeb.createPasskeyWallet(isNewPasskeyFlow)) {
                this.updateWalletData();
                await this.thirdWeb.waitSmartWalletConnection();
                this.inventory.mainWalletAddress = this.smartWalletAddress ?? this.address.value;
                return true;
            }
            return false;
        } catch (e: unknown) {
            this.logger.error("Exception on creating passkey wallet", e);
            return false;
        }
    }

    public async signMessage(nonce: string): Promise<string> {
        if (!this.connected.value) {
            this.logger.warn(`Unable to sign nonce from not authorized wallet`);
            return null;
        }

        return await this.thirdWeb.signMessage(nonce);
    }

    public async updateUSDCBalance(): Promise<string> {
        const value = await this.thirdWeb.updateUSDCBalance();
        this.balanceUSDC.setValue(value);
        return value;
    }

    public async updateMONBalance(): Promise<string> {
        const value = await this.thirdWeb.updateMONBalance();
        this.balanceMON.setValue(value);
        return value;
    }

    public async updateErc20Balance(chain: Chain, erc20Address: string): Promise<string> {
        const value = await this.thirdWeb.updateErc20Balance(chain, erc20Address);
        if (this.isTargetChain(chain)) {
            this.erc20BalancesOnBase.set(erc20Address, value);
        } else {
            this.erc20BalancesOnL1.set(erc20Address, value);
        }

        return value;
    }

    public async getTokenSymbol(chain: Chain, erc20Address: string): Promise<string> {
        const value = await this.thirdWeb.getTokenSymbol(chain, erc20Address);

        return value;
    }

    public async switchNetwork(): Promise<boolean> {
        throw new Error("Not implemented");
    }

    public async paymentBuildingWithMint(buildingId: string, cancellationToken: CancellationToken, couponBenefitIds?: Array<string>): Promise<PaymentResult> {
        if (this.address.value == null || this.address.value === "") {
            this.logger.debug(`Address is not defined, unable to start payment. Address: ${this.address.value}`);
            return;
        }
        const myNftBuildings = this.inventory.buildingsNFT;
        try {
            const result = await this.crossmint.paymentBuildingWithMint(buildingId, cancellationToken, couponBenefitIds);
            if (result?.status != null) {
                if (result.status == PaymentResult.SUCCESS) {
                    await this.crossmint.successPayment(result.txId, result.tokenId);
                }
                return result.status;
            }
            return (await this.asyncOperationNotification.autoShowHide(this.checkBuildingsCountIncreased(myNftBuildings)))
                ? PaymentResult.SUCCESS
                : PaymentResult.FAILURE;
        } catch (e: unknown) {
            if (await this.asyncOperationNotification.autoShowHide(this.checkBuildingsCountIncreased(myNftBuildings))) {
                return PaymentResult.SUCCESS;
            }
            if (e instanceof CrossmintPaymentTimeout) {
                this.logger.debug("Payment end with timeout");
                return PaymentResult.CANCELLED;
            }
            this.logger.error("Exception on processing crossmint payment: ", e);
            return PaymentResult.FAILURE;
        }
    }

    private async checkBuildingsCountIncreased(previousBuildings: ReadonlyArray<NFTBuilding>): Promise<boolean> {
        await this.inventory.updateBuildings();
        return previousBuildings.length < this.inventory.buildingsNFT.length;
    }

    public async paymentBuildingWithoutMint(tokenId: string, cancellationToken: CancellationToken): Promise<PaymentResult> {
        if (this.address.value == null || this.address.value === "") {
            this.logger.debug(`Address is not defined, unable to start payment. Address: ${this.address.value}`);
            return PaymentResult.FAILURE;
        }
        const formatedTokenId = BigInt(tokenId);
        try {
            const result = await this.crossmint.paymentBuildingWithoutMint(this.smartWalletAddress, tokenId, cancellationToken);
            await this.thirdWeb.waitSmartWalletConnection();
            if (result?.status != null) {
                if (result.status == PaymentResult.SUCCESS) {
                    await this.crossmint.successPayment(result.txId, result.tokenId);
                }
                return result.status;
            }
            return (await this.asyncOperationNotification.autoShowHide(this.checkBuildingPurchased(formatedTokenId)))
                ? PaymentResult.SUCCESS
                : PaymentResult.FAILURE;
        } catch (e: unknown) {
            if (await this.asyncOperationNotification.autoShowHide(this.checkBuildingPurchased(formatedTokenId))) {
                return PaymentResult.SUCCESS;
            }
            if (e instanceof CrossmintPaymentTimeout) {
                this.logger.debug("Payment end with timeout");
                return PaymentResult.CANCELLED;
            }
            this.logger.error("Exception on processing building crossmint payment: ", e);
            return PaymentResult.FAILURE;
        }
    }

    private async checkBuildingPurchased(tokenId: BigInt): Promise<boolean> {
        await this.inventory.updateBuildings();
        return this.inventory.buildingExist(tokenId);
    }

    public async storePayment(tokenId: string, price: number, cancellationToken: CancellationToken): Promise<PaymentResult> {
        if (this.address.value == null || this.address.value === "") {
            this.logger.debug(`Address is not defined, unable to start payment. Address: ${this.address.value}`);
            return PaymentResult.FAILURE;
        }
        const formatedTokenId = BigInt(tokenId);
        const quantityBeforePurchase = this.inventory.getPackageQuantityOwned(formatedTokenId);
        try {
            const result = await this.crossmint.storePayment(this.smartWalletAddress, tokenId, price, cancellationToken);
            await this.thirdWeb.waitSmartWalletConnection();
            if (result?.status != null) {
                return result.status;
            }
            return (await this.asyncOperationNotification.autoShowHide(this.checkPackagePurchased(formatedTokenId, quantityBeforePurchase)))
                ? PaymentResult.SUCCESS
                : PaymentResult.FAILURE;
        } catch (e: unknown) {
            if (await this.asyncOperationNotification.autoShowHide(this.checkPackagePurchased(formatedTokenId, quantityBeforePurchase))) {
                return PaymentResult.SUCCESS;
            }
            if (e instanceof CrossmintPaymentTimeout) {
                this.logger.debug("Payment end with timeout");
                return PaymentResult.CANCELLED;
            }
            this.logger.error("Exception on processing store crossmint payment: ", e);
            return PaymentResult.FAILURE;
        }
    }

    public async ethL1Erc20StorePayment(erc20Address: string, tokenId: string, price: number): Promise<PaymentResult> {
        try {
            if (!(await this.thirdWeb.approveL1Erc20Transaction(erc20Address, price))) {
                this.logger.debug("Unable to make Mon Store payment, not approved");
                return PaymentResult.FAILURE;
            }
            const result = await this.thirdWeb.processL1StorePackageClaimTransaction(BigInt(tokenId));
            if (result.status) {
                await this.networkClient.successPurchaseStorePackageOnL1(result.transactionHash);
                return PaymentResult.SUCCESS;
            } else {
                return PaymentResult.FAILURE;
            }
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on purchase Mon store package: ${e}`);
            return PaymentResult.FAILURE;
        }
    }

    public async erc20StorePayment(chain: Chain, erc20Address: string, tokenId: string, price: number): Promise<PaymentResult> {
        try {
            if (!(await this.thirdWeb.approveErc20Transaction(chain, erc20Address, this.web3Config.packagesCollectionAddress, price))) {
                this.logger.debug("Unable to make Store Package payment with ERC20 token, not approved");
                return PaymentResult.FAILURE;
            }
            const result = await this.thirdWeb.processStorePackageClaimTransaction(chain, BigInt(tokenId));
            if (result.status) {
                await this.networkClient.successPurchaseStorePackageOnBase(result.transactionHash);
                return PaymentResult.SUCCESS;
            } else {
                return PaymentResult.FAILURE;
            }
        } catch (e: unknown) {
            this.logger.debug(`Exception on purchase Store Package with ERC20 token: ${e}`);
            return PaymentResult.FAILURE;
        }
    }

    private async checkPackagePurchased(tokenId: BigInt, initialQuantity: BigInt): Promise<boolean> {
        await this.inventory.updatePackages();
        return this.inventory.getPackageQuantityOwned(tokenId) > initialQuantity;
    }

    public async burnPackage(nftPackage: NFTPackage): Promise<string | boolean> {
        const tokenId = nftPackage.id;
        const quantityBeforeBurn = this.inventory.getPackageQuantityOwned(tokenId);
        try {
            const transactionHash = await this.thirdWeb.burnNFTPackage(nftPackage);
            if (transactionHash == null) {
                await this.inventory.updatePackages();
                const burned = this.inventory.getPackageQuantityOwned(tokenId) < quantityBeforeBurn;
                if (burned) {
                    this.inventory.removePackage(tokenId);
                }
                return burned;
            }
            this.inventory.removePackage(tokenId);
            return transactionHash;
        } catch (e: unknown) {
            this.logger.error("Exception on burn package: ", e);
            await this.inventory.updatePackages();
            return this.inventory.getPackageQuantityOwned(tokenId) < quantityBeforeBurn;
        }
    }

    public async burnGiftPackage(nftGiftPackage: NFTGiftPackage): Promise<string | boolean> {
        const tokenId = nftGiftPackage.id;
        const quantityBeforeBurn = this.inventory.getGiftPackageQuantityOwned(tokenId);
        try {
            const transactionHash = await this.thirdWeb.burnNFTGiftPackage(nftGiftPackage);
            if (transactionHash == null) {
                await this.inventory.updateGiftPackages();
                const burned = this.inventory.getGiftPackageQuantityOwned(tokenId) < quantityBeforeBurn;
                if (burned) {
                    this.inventory.removeGiftPackage(tokenId);
                }
                return burned;
            }
            this.inventory.removeGiftPackage(tokenId);
            return transactionHash;
        } catch (e: unknown) {
            this.logger.error("Exception on burn package: ", e);
            await this.inventory.updateGiftPackages();
            return this.inventory.getGiftPackageQuantityOwned(tokenId) < quantityBeforeBurn;
        }
    }

    public async listInToReservoir(buildingTokenId: BigInt, price: string): Promise<string> {
        const result = await this.reservoirListInUtil.listIn(this.thirdWeb.account, this.web3Config.buildingCollectionAddress, buildingTokenId, price);
        this.logger.debug("listInToReservoir result: " + JSON.stringify(result));
        if (result === "unauthorized") {
            this.logger.debug("Unauth error on list in to reservoir");
            await this.disconnect();
            return null;
        }
        return result?.orderId;
    }

    public async buyBuildingThroughReservoir(orderId: string): Promise<PaymentResult> {
        if (this.address.value == null || this.address.value === "") {
            this.logger.debug(`Address is not defined, unable to start payment. Address: ${this.address.value}`);
            return PaymentResult.FAILURE;
        }

        await this.thirdWeb.waitSmartWalletConnection();
        const result = await this.reservoirListInUtil.buyNft(this.thirdWeb.account, orderId);

        if (result === "unauthorized") {
            this.logger.debug("Unauth error on accepting offer with reservoir");
            await this.disconnect();
            return PaymentResult.FAILURE;
        }

        return result?.isTradeCompleted ? PaymentResult.SUCCESS : PaymentResult.FAILURE;
    }

    public async removeFromReservoir(orderId: string): Promise<boolean> {
        const result = await this.reservoirListInUtil.remove(this.thirdWeb.account, orderId);
        if (result === "unauthorized") {
            this.logger.debug("Unauth error on remove from reservoir");
            await this.disconnect();
            return false;
        }
        return result;
    }

    public async claimGiftNft(mintRequestDto: MintRequestDto): Promise<boolean> {
        try {
            const giftPackagesCount = this.inventory.giftPackagesNFT.length;
            if (await this.thirdWeb.claimGiftNft(new MintRequest(mintRequestDto))) {
                return true;
            }
            return giftPackagesCount < (await this.inventory.updateGiftPackages()).length;
        } catch (e: unknown) {
            this.logger.error("Unable to claim gift nft: ", e);
            return false;
        }
    }

    public async disconnect(): Promise<void> {
        if (!this.connected.value) {
            this.logger.debug(`Attemp of disconnect not connected wallet`);
            return;
        }

        await this.thirdWeb?.disconnect();
        this.address.setValue(null);
        this.connected.setValue(false);
        this.supportedChainSelected.setValue(false);
        this.inventory.mainWalletAddress = null;
        this.inventory.clear();
    }

    public async connectExternalWallet(walletType: ExternalWalletType): Promise<Array<string>> {
        if (!this.externalWallets.has(walletType)) {
            switch (walletType) {
                case ExternalWalletType.METAMASK:
                    this.externalWallets.set(walletType, new MetaMask(this.web3Config, this.variableSaver, this.gameInfo, this.logger, this.timeoutObserver));
                    break;
                case ExternalWalletType.COINBASE:
                    this.externalWallets.set(walletType, new CoinBase(this.web3Config, this.logger, this.timeoutObserver));
                    break;
                default:
                    throw new Error(`External wallet ${walletType} not implemented`);
            }
        }
        const wallet = this.externalWallets.get(walletType);
        if (utils.isMobile.any && wallet.type === ExternalWalletType.METAMASK && wallet.connected) {
            await wallet.disconnect();
        }
        if (wallet.connected || (await wallet.connect())) {
            return wallet.addresses;
        }
        return null;
    }

    public async signExternalWalletMessage(walletType: ExternalWalletType, address: string, nonce: string): Promise<string> {
        if (!this.externalWallets.has(walletType)) {
            this.logger.error(`Unable to sign message using ${walletType}, that not defined`);
            return null;
        }
        const wallet = this.externalWallets.get(walletType);
        return await wallet.signMessage(address, nonce);
    }

    public addExternalWallet(walletAddress: string): void {
        this.inventory.addInventory(walletAddress);
    }

    public removeExternalWallet(walletAddress: string): void {
        this.inventory.removeInventory(walletAddress);
    }

    public hasExternalWalletInInventory(address: string): boolean {
        return this.inventory.walletAddressExist(address);
    }

    public async waitSmartWalletConnection(): Promise<void> {
        if (!this.connected) {
            this.logger.debug("Awaiting connection of smart wallet with not connected inAppWallet");
            return;
        }
        return await this.thirdWeb.waitSmartWalletConnection();
    }

    private updateWalletData(): void {
        this.connected.setValue(this.thirdWeb?.connected);
        this.address.setValue(this.thirdWeb?.address);
        this.supportedChainSelected.setValue(this.thirdWeb?.chainId === ChainUtils.idToHex(this.web3Config.targetChain.id));
        this.chainName.setValue(this.supportedChainSelected.value ? this.web3Config.targetChain.name : ChainUtils.nameFromId(this.thirdWeb?.chainId));
        if (this.connected.value) {
            this.thirdWeb.waitSmartWalletConnection().then(() => {
                this.inventory.mainWalletAddress = this.smartWalletAddress;
            });
        } else {
            this.inventory.mainWalletAddress = null;
            this.inventory.clear();
        }
    }

    public buildingExistOnInternalWallet(tokenId: string): boolean {
        return this.inventory.buildingExistOnInternalWallet(BigInt(tokenId));
    }

    public async sendL1Erc20Tokens(erc20Address: string, address: string, value: number): Promise<boolean> {
        return await this.thirdWeb.sendL1Erc20Tokens(erc20Address, address, value);
    }

    public async sendErc20Tokens(chain: Chain, erc20Address: string, address: string, value: number): Promise<boolean> {
        return await this.thirdWeb.sendErc20Tokens(chain, erc20Address, address, value);
    }

    public async purchaseL1Building(buildingId: string, couponBenefitIds?: Array<string>): Promise<PaymentResult> {
        try {
            const mint = await this.networkClient.mpPrepareMint(buildingId, couponBenefitIds);
            const result = await this.thirdWeb.purchaseL1Building(mint);
            if (result.status) {
                await this.networkClient.successPurchaseBuilding(result.transactionHash, undefined);
                return PaymentResult.SUCCESS;
            } else {
                return PaymentResult.FAILURE;
            }
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on purchase Mon building: ${e}`);
            return PaymentResult.FAILURE;
        }
    }

    public async purchaseBuildingWithCrypto(chain: Chain, buildingId: string, couponBenefitIds?: Array<string>): Promise<PaymentResult> {
        try {
            const mint = await this.networkClient.mpPrepareMint(buildingId, couponBenefitIds);
            const result = await this.thirdWeb.purchaseBuildingWithCrypto(chain, mint);
            if (result.status) {
                await this.networkClient.successPurchaseBuilding(result.transactionHash, undefined);
                return PaymentResult.SUCCESS;
            } else {
                return PaymentResult.FAILURE;
            }
        } catch (e: unknown) {
            this.logger.debug(`Exception on purchase building with crypto: ${e}`);
            return PaymentResult.FAILURE;
        }
    }

    public isTargetChain(chain: Chain): boolean {
        return chain === this.web3Config.targetChain;
    }

    public getUsdcTokenAddress(): string {
        return this.web3Config.reservoirDevUsdcCurrency;
    }

    public getMonTokenAddress(): string {
        return this.web3Config.monCurrencyAddress;
    }
}
